import { useLayoutEffect, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { alertaGeneral } from '../../utils/alertas';
import apiClient from '../../utils/axiosPetition';

const OAUTH_TIMEOUT_MS = 5000;  // 5 segundos: si el backend no responde, fallar rápido
const SAFETY_TIMEOUT_MS = 8000; // 8 segundos máximo en esta pantalla

const OAuthCallback = () => {
    const navigate = useNavigate();
    const processedRef = useRef(false); // Para evitar procesar múltiples veces
    const hasNavigatedRef = useRef(false);

    // Timeout de seguridad: si tras SAFETY_TIMEOUT_MS no hemos redirigido, redirigir a login
    useEffect(() => {
        const safetyTimer = setTimeout(async () => {
            if (hasNavigatedRef.current) return;
            console.warn('OAuthCallback: timeout de seguridad, redirigiendo a login');
            hasNavigatedRef.current = true;
            await alertaGeneral({
                titulo: 'Tiempo agotado',
                messageHtml: 'La autenticación tardó demasiado. Por favor, intenta de nuevo.',
                isError: true
            });
            navigate('/login', { replace: true });
        }, SAFETY_TIMEOUT_MS);
        return () => clearTimeout(safetyTimer);
    }, [navigate]);

    useLayoutEffect(() => {
        // Si ya procesamos, no hacer nada
        if (processedRef.current) {
            return;
        }

        // Marcar como procesado INMEDIATAMENTE para prevenir ejecuciones múltiples
        processedRef.current = true;

        const markNavigated = () => { hasNavigatedRef.current = true; };

        // Ejecutar el procesamiento de forma asíncrona para evitar problemas de render
        const processOAuth = async () => {
            // Leer parámetros directamente de la URL para evitar problemas con searchParams
            const urlParams = new URLSearchParams(window.location.search);
            const code = urlParams.get('code');
            let token = urlParams.get('token');
            const error = urlParams.get('error');
            let role = urlParams.get('role');
            let patientId = urlParams.get('patientId');
            let patientName = urlParams.get('patientName');
            let patientEmail = urlParams.get('patientEmail');
            let patientProfileComplete = urlParams.get('patientProfileComplete');
            let doctorId = urlParams.get('doctorId');
            let doctorName = urlParams.get('doctorName');
            let imageProfile = urlParams.get('imageProfile');
            const email = urlParams.get('email');
            let emailFromCode = null;
            let requiresMembershipPaymentFromApi = false;
            let doctorEstadoFromApi = null;
            let doctorTipoPerfilFromApi = null;
            let doctorPrestacionServicioFromApi = null;

            // Si viene con código de un solo uso, canjear por token y datos (evita URLs largas que se truncan)
            if (code && !error) {
                try {
                    const res = await apiClient.get(`/api/auth/oauth-token?code=${encodeURIComponent(code)}`, {
                        timeout: OAUTH_TIMEOUT_MS
                    });
                    token = res.data?.token;
                    role = res.data?.role;
                    patientId = res.data?.patientId;
                    patientName = res.data?.patientName;
                    patientEmail = res.data?.patientEmail;
                    patientProfileComplete = res.data?.patientProfileComplete;
                    doctorId = res.data?.doctorId || doctorId;
                    doctorName = res.data?.doctorName || doctorName;
                    imageProfile = res.data?.imageProfile || imageProfile;
                    if (res.data?.email) emailFromCode = res.data.email;
                    if (res.data?.requiresMembershipPayment === true) requiresMembershipPaymentFromApi = true;
                    if (res.data?.doctorEstado) doctorEstadoFromApi = res.data.doctorEstado;
                    if (res.data?.tipoPerfil) doctorTipoPerfilFromApi = res.data.tipoPerfil;
                    if (typeof res.data?.prestacionServicio === 'boolean') {
                        doctorPrestacionServicioFromApi = res.data.prestacionServicio;
                    }
                } catch (err) {
                    console.error('Error canjeando código OAuth:', err);
                    markNavigated();
                    await alertaGeneral({
                        titulo: 'Error en autenticación',
                        messageHtml: err?.code === 'ECONNABORTED'
                            ? 'El servidor no respondió a tiempo. Comprueba tu conexión e intenta de nuevo.'
                            : 'No se pudo completar el inicio de sesión. El enlace pudo haber expirado. Intenta de nuevo.',
                        isError: true
                    });
                    navigate('/login', { replace: true });
                    return;
                }
            }

            // Normalizar rol (backend puede enviar 'patient' o variantes)
            const roleNormalized = (role != null && role !== '') ? String(role).toLowerCase() : '';

            console.log('OAuthCallback - Parámetros recibidos:', { token: !!token, error, role: roleNormalized, email });

            // Si el correo ya está registrado como paciente (intentando registro doctor)
            if (error === 'email_registered_as_patient') {
                markNavigated();
                sessionStorage.removeItem('oauth_return_path');
                await alertaGeneral({
                    titulo: 'Correo ya registrado',
                    messageHtml: 'Este correo ya está registrado como paciente. Inicia sesión desde la opción de pacientes o usa otro correo para registrarte como prestador.',
                    isError: true
                });
                navigate('/login', { replace: true });
                return;
            }

            // Si hay error de usuario no registrado
            if (error === 'user_not_registered') {
                console.log('Usuario no registrado, email:', email);
                markNavigated();
                const oauthAceptoAutorizacion = sessionStorage.getItem('oauth_aceptoAutorizacion');
                const oauthReturnPath = sessionStorage.getItem('oauth_return_path');
                sessionStorage.removeItem('oauth_aceptoAutorizacion');
                sessionStorage.removeItem('oauth_return_path');

                if (oauthAceptoAutorizacion === 'true') {
                    // Si venía de registro-pagado-usuario, volver ahí para completar registro y pago
                    if (oauthReturnPath === '/registro-pagado-usuario') {
                        navigate('/registro-pagado-usuario', {
                            replace: true,
                            state: {
                                emailFromOAuth: email || urlParams.get('email') || '',
                                fromOAuth: true,
                                aceptoAutorizacion: true
                            }
                        });
                        return;
                    }
                    // Viene de registro gratuito, redirigir al formulario de registro
                    navigate('/registro-gratuito-usuario', {
                        replace: true,
                        state: {
                            emailFromOAuth: email || '',
                            fromOAuth: true,
                            aceptoAutorizacion: true
                        }
                    });
                } else {
                    // Viene de login, redirigir al login con mensaje
                    console.log('Viene de login, redirigiendo al login');
                    const emailParam = email ? `&email=${encodeURIComponent(email)}` : '';
                    navigate(`/login?oauth_error=user_not_registered${emailParam}`, { replace: true });
                }
                return;
            }

            // Si hay otros errores, procesarlos
            if (error) {
                markNavigated();
                if (error === 'user_disabled') {
                    await alertaGeneral({
                        titulo: 'Cuenta desactivada',
                        messageHtml: 'Tu cuenta ha sido desactivada. Contacta al administrador.',
                        isWarning: true
                    });
                    navigate('/login');
                } else if (error === 'oauth_not_configured') {
                    await alertaGeneral({
                        titulo: 'Error en autenticación',
                        messageHtml: 'OAuth no está configurado. Por favor, usa el formulario de registro.',
                        isError: true
                    });
                    navigate('/registro-gratuito-usuario');
                } else {
                    console.log('Error OAuth desconocido:', error);
                    await alertaGeneral({
                        titulo: 'Error en autenticación',
                        messageHtml: 'Hubo un problema con la autenticación. Por favor, intenta nuevamente.',
                        isError: true
                    });
                    navigate('/registro-gratuito-usuario');
                }
                return;
            }

            // Si hay token y es doctor, procesar login/registro exitoso de doctor
            if (token && roleNormalized === 'doctor') {
                markNavigated();
                const returnPathFromUrl = urlParams.get('return_path');
                const oauthReturnPath = sessionStorage.getItem('oauth_return_path');
                sessionStorage.removeItem('oauth_return_path');
                // Exigir pago si vino de registro-pagado O si el backend indica que no tiene membresía pagada (p. ej. se registró con Google y no ha pagado)
                const mustRequirePayment = returnPathFromUrl === 'registro-pagado' || oauthReturnPath === '/registro-pagado' || requiresMembershipPaymentFromApi;

                localStorage.setItem('token', token);
                localStorage.setItem('role', 'doctor');
                if (doctorId) localStorage.setItem('doctorId', doctorId);
                if (doctorName) localStorage.setItem('doctorName', doctorName);
                if (patientId) localStorage.setItem('patientId', patientId);
                if (imageProfile) localStorage.setItem('imageProfile', imageProfile);
                if (doctorTipoPerfilFromApi) {
                    localStorage.setItem('doctorTipoPerfil', doctorTipoPerfilFromApi);
                } else {
                    localStorage.removeItem('doctorTipoPerfil');
                }
                if (typeof doctorPrestacionServicioFromApi === 'boolean') {
                    localStorage.setItem('doctorPrestacionServicio', String(doctorPrestacionServicioFromApi));
                } else {
                    localStorage.removeItem('doctorPrestacionServicio');
                }

                if (mustRequirePayment) {
                    await alertaGeneral({
                        titulo: 'Cuenta verificada',
                        messageHtml: 'Acepta la autorización de datos y completa el pago de tu membresía para continuar como prestador.',
                        isWarning: false
                    });
                    navigate('/registro-pagado', {
                        replace: true,
                        state: {
                            fromOAuthDoctor: true,
                            needsPayment: true,
                            doctorName: doctorName || '',
                            email: emailFromCode || email || ''
                        }
                    });
                    return;
                }

                // Misma lógica que login con correo: doctor nuevo / en revisión va a perfil
                const doctorPath = (doctorEstadoFromApi === 'no_aprobado' || doctorEstadoFromApi === 'en_revision')
                    ? '/doctor/perfil'
                    : '/doctor/dashboard';
                window.location.replace(window.location.origin + doctorPath);
                return;
            }

            // Si hay token pero el rol no es patient ni doctor, redirigir a login
            if (token && roleNormalized !== 'patient') {
                console.warn('OAuthCallback: token recibido pero rol no es patient ni doctor:', role);
                markNavigated();
                await alertaGeneral({
                    titulo: 'Acceso no disponible',
                    messageHtml: 'Tu cuenta no tiene acceso al panel de paciente. Inicia sesión con la opción correcta.',
                    isError: true
                });
                navigate('/login', { replace: true });
                return;
            }

            // Si hay token, procesar login exitoso de paciente
            if (token && roleNormalized === 'patient') {
                markNavigated();
                const oauthReturnPath = sessionStorage.getItem('oauth_return_path');
                sessionStorage.removeItem('oauth_return_path');

                // Guardar datos en localStorage (similar a LoginVerificacion)
                localStorage.setItem('token', token);
                localStorage.setItem('role', role);
                if (patientId) localStorage.setItem('patientId', patientId);
                if (patientName) localStorage.setItem('patientName', patientName);
                if (patientEmail) localStorage.setItem('patientEmail', patientEmail);
                if (patientProfileComplete !== undefined && patientProfileComplete !== null) {
                    localStorage.setItem('patientProfileComplete', String(patientProfileComplete));
                }
                if (imageProfile) localStorage.setItem('imageProfile', imageProfile);

                // Si venía de registro-pagado-usuario, volver ahí para pagar la membresía
                if (oauthReturnPath === '/registro-pagado-usuario') {
                    await alertaGeneral({
                        titulo: 'Cuenta verificada',
                        messageHtml: 'Completa el pago de tu membresía Premium para continuar.',
                        isWarning: false
                    });
                    navigate('/registro-pagado-usuario', {
                        replace: true,
                        state: {
                            fromOAuthPaidUser: true,
                            needsPayment: true,
                            emailFromOAuth: patientEmail || '',
                            patientName: patientName || '',
                            patientId: patientId || '',
                            aceptoAutorizacion: true
                        }
                    });
                    return;
                }

                // Redirección: pacientes van al directorio de profesionales para encontrar doctor (nuevos y recurrentes)
                window.location.replace(window.location.origin + '/patient/directorio-profesionales');
                return;
            } else if (!token && !error) {
                // Si no hay token ni error, podría ser que el backend redirigió directamente
                markNavigated();
                console.log('No hay token ni error, redirigiendo a registro');
                await alertaGeneral({
                    titulo: 'Error',
                    messageHtml: 'No se pudo completar la autenticación. Por favor, intenta nuevamente.',
                    isError: true
                });
                navigate('/registro-gratuito-usuario');
            }
        };

        // Ejecutar de forma asíncrona para evitar problemas de render
        processOAuth().catch(error => {
            console.error('Error procesando OAuth:', error);
            hasNavigatedRef.current = true;
            navigate('/login', { replace: true });
        });
    // Ejecutar solo una vez al montar
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Array vacío porque leemos directamente de window.location.search

    return (
        <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1f94c2ff] mx-auto"></div>
                <p className="mt-4 text-gray-600">Procesando autenticación...</p>
            </div>
        </div>
    );
};

export default OAuthCallback;
